import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Eye, Zap, Shield, Waves, Play } from "lucide-react";
import { PARTNER_LINK } from "@/lib/constants";
import { trackEvent } from "@/lib/tracking";
import heilys4 from "@/assets/heilys_4.webp";
import heilys3 from "@/assets/heilys_3.webp";

const YOUTUBE_ID = "LGxkShLRw1k";
const YOUTUBE_THUMB = `https://i.ytimg.com/vi/${YOUTUBE_ID}/hqdefault.jpg`;

const highlights = [
  {
    icon: Eye,
    label: "Vision-Modus",
    detail: "670 nm speziell für Augenkomfort – laut Hersteller ohne Schutzbrille nutzbar",
  },
  {
    icon: Waves,
    label: "6 Wellenlängen",
    detail: "630, 660, 670, 810, 830 und 850 nm in einem kompakten Gerät",
  },
  {
    icon: Zap,
    label: "3 Betriebsmodi",
    detail: "Kombi-Modus, Pulsmodus und Vision-Modus für unterschiedliche Anwendungen",
  },
  {
    icon: Shield,
    label: "Flimmerfrei & Low-EMF",
    detail: "36 Dual-Chip-LEDs, 30 W, aktive Kühlung und sehr niedrige EMF-Werte",
  },
];

const fadeUp = {
  hidden: { opacity: 0.4, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.3 },
  }),
};

const ProductSpotlightSection = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
  <section id="produkt-spotlight" className="py-10 md:py-20">
    <div className="container">
      <h2 className="text-center font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
        heilys Gen30+Vision – Specs &amp; erste heilys Gen30 Erfahrungen
      </h2>
      <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-muted-foreground md:mt-3 md:text-base">
        Das kompakte heilys Gen30 Rotlichtgerät mit integriertem Vision-Modus für gezielte Anwendungen an Gesicht, Muskeln und Gelenken. Hier unsere unabhängige Einordnung und erste heilys Gen30 Erfahrungen auf Basis der offiziellen Herstellerangaben und öffentlich verfügbarer Reviews.
      </p>

      <div className="mt-8 md:mt-10 grid items-start gap-6 md:gap-8 lg:grid-cols-2">
        {/* Left: YouTube Short embed */}
          <motion.div
          initial={{ opacity: 0.4, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3 }}
          className="flex justify-center"
        >
          <div className="w-full max-w-[260px] sm:max-w-[320px] overflow-hidden rounded-2xl border border-border/50 bg-card shadow-xl shadow-black/20">
            <div className="relative w-full" style={{ paddingBottom: "177.78%" }}>
              {videoLoaded ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_ID}?autoplay=1`}
                  title="heilys Gen30+Vision – Kurzvorstellung"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setVideoLoaded(true)}
                  className="absolute inset-0 flex h-full w-full cursor-pointer items-center justify-center bg-black"
                  aria-label="Video abspielen: heilys Gen30+Vision Kurzvorstellung"
                >
                  <img
                    src={YOUTUBE_THUMB}
                    alt="heilys Gen30+Vision Video-Vorschau"
                    className="absolute inset-0 h-full w-full object-cover opacity-80"
                    loading="lazy"
                  />
                  <div className="relative z-10 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-primary/90 shadow-lg transition-transform hover:scale-110">
                    <Play className="h-6 w-6 sm:h-7 sm:w-7 fill-primary-foreground text-primary-foreground" />
                  </div>
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Right: Product details */}
        <div className="space-y-4 sm:space-y-5">
          {/* Key highlights */}
          <div className="grid gap-3 grid-cols-2">
            {highlights.map((h, i) => (
              <motion.div
                key={h.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUp}
                className="rounded-xl border border-border/50 bg-card p-3 sm:p-4 transition-all hover:border-primary/20"
              >
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center justify-center rounded-lg bg-primary/10 p-1.5">
                    <h.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-xs sm:text-sm font-semibold text-foreground">{h.label}</h3>
                </div>
                <p className="mt-1.5 sm:mt-2 text-[11px] sm:text-xs leading-relaxed text-muted-foreground">{h.detail}</p>
              </motion.div>
            ))}
          </div>

          {/* Editorial assessment */}
          <motion.div
            initial={{ opacity: 0.4, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, delay: 0.15 }}
            className="rounded-xl border border-border/50 bg-card p-4 sm:p-5"
          >
            <h3 className="font-display text-xs sm:text-sm font-semibold text-foreground">Unsere Einordnung &amp; erste Gen30 Erfahrungen</h3>
            <div className="mt-2 space-y-2 text-xs sm:text-sm leading-relaxed text-muted-foreground">
              <p>
                Das Gen30+Vision ist eher für gezielte, lokale Anwendungen konzipiert – nicht für Ganzkörper-Setups. Die kompakte Bauform, die moderate Leistung von 30 W und die Positionierung für Gesicht, Nacken, Muskeln und Gelenke sprechen klar dafür.
              </p>
              <p>
                Die Besonderheit ist der Vision-Modus mit 670 nm, der laut heilys ohne Schutzbrille nutzbar sein soll. Im Kombi- und Pulsmodus empfiehlt heilys dagegen die mitgelieferte Schutzbrille. Die technischen Daten (bis 95 mW/cm² bei 7,5 cm, 16° Abstrahlwinkel) passen zu einem fokussierten Nahbereich-Gerät.
              </p>
              <p className="text-[11px] sm:text-xs text-muted-foreground/70 italic">
                Hinweis: Die genannten Angaben basieren auf der offiziellen heilys Produktseite und dem Datenblatt. heilys weist selbst darauf hin, dass die Produkte nicht als medizinische Geräte klassifiziert sind.
              </p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0.4 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3"
          >
            <a
              href={PARTNER_LINK}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={() => trackEvent("affiliate_click", "spotlight")}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:brightness-110 w-full sm:w-auto"
            >
              Gen30+Vision im heilys Shop ansehen
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            <span className="text-xs text-muted-foreground">
              Mit Code LONGEVITY 5 % Rabatt sichern
            </span>
          </motion.div>
        </div>
      </div>

      {/* Product image gallery */}
      <div className="mt-8 md:mt-12 grid gap-4 sm:gap-6 grid-cols-2">
        <motion.figure
          initial={{ opacity: 0.4, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden rounded-xl sm:rounded-2xl border border-border/50 bg-card"
        >
          <img
            src={heilys4}
            alt="heilys Gen30+Vision Rotlichtlampe bei der Gesichtsanwendung – Nahinfrarot und Rotlicht für Hautpflege"
            title="heilys Gen30+Vision Rotlichtlampe – Gesichtsanwendung mit Rabattcode LONGEVITY"
            width={640}
            height={800}
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 45vw, 300px"
            className="w-full object-cover"
          />
          <figcaption className="px-3 py-2 sm:px-4 sm:py-3 text-[11px] sm:text-xs text-muted-foreground">
            Gezielte Rotlichtanwendung im Gesichtsbereich mit dem heilys Gen30+Vision
          </figcaption>
        </motion.figure>

        <motion.figure
          initial={{ opacity: 0.4, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, delay: 0.08 }}
          className="overflow-hidden rounded-xl sm:rounded-2xl border border-border/50 bg-card"
        >
          <img
            src={heilys3}
            alt="heilys Gen30+Vision Rotlichtgerät – kompaktes Design mit 36 Dual-Chip-LEDs"
            title="heilys Gen30+Vision Rotlichtgerät – 5 % Rabatt mit Code LONGEVITY"
            width={640}
            height={800}
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 45vw, 300px"
            className="w-full object-cover"
          />
          <figcaption className="px-3 py-2 sm:px-4 sm:py-3 text-[11px] sm:text-xs text-muted-foreground">
            Das kompakte Gen30+Vision liegt gut in der Hand und lässt sich flexibel positionieren
          </figcaption>
        </motion.figure>
      </div>
    </div>
  </section>
  );
};

export default ProductSpotlightSection;
